import * as React from 'react'
import {Box, Accordion, AccordionSummary, AccordionDetails, Typography, Chip, Button} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import PowerOutlinedIcon from '@mui/icons-material/PowerOutlined'
import {sectionTitleSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, SUCCESS, ERROR, MUTED, withOpacity} from '../../../styles/foundation/colors'
import {pct as fmtPct, int as fmtInt} from './format'
import {DWS_STEPS, DWS_SLOTS, DWS_RETAIN, DWS_API_TIP} from './data/dwsCopy'

const ICONS = {
  video: VideocamOutlinedIcon, box: Inventory2OutlinedIcon, package: ScaleOutlinedIcon,
  tags: LocalOfferOutlinedIcon, ban: BlockOutlinedIcon, timer: TimerOutlinedIcon, plug: PowerOutlinedIcon,
}

const TONE = {ok: SUCCESS, danger: ERROR, neutral: MUTED}

// The page's shot list and the upload form's drop zones are meant to agree —
// the mockup had already drifted once, scoring a fifth "package with label"
// shot that the page never asked for. Fail loudly at module load instead.
const SLOT_KEYS = new Set(DWS_SLOTS.map((s) => s.k))
DWS_STEPS.forEach((step) => {
  ;(step.slots || []).forEach((k) => {
    if (!SLOT_KEYS.has(k)) throw new Error(`DWS_STEPS row "${step.k}" names slot "${k}", which DWS_SLOTS does not define`)
  })
})

function Row({icon, title, chip, chipTone, stat, body, action, expanded, onToggle}) {
  const Icon = ICONS[icon] || TimerOutlinedIcon
  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      sx={{border: `1px solid ${BORDER}`, borderRadius: '10px !important', mb: 1, '&::before': {display: 'none'}}}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, pr: 1}}>
          <Icon sx={{fontSize: 17, color: SUBTLE, flexShrink: 0}} />
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], color: NAVY}}>
            {title}
          </Typography>
          <Box sx={{ml: 'auto', flexShrink: 0}}>
            {stat
              ? <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>{stat}</Typography>
              : (
                <Chip
                  size='small'
                  label={chip}
                  sx={{
                    bgcolor: withOpacity(TONE[chipTone] || MUTED, 0.12), color: TONE[chipTone] || MUTED,
                    fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], height: 22,
                  }}
                />
              )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.7}}>{body}</Typography>
        {action && <Box sx={{mt: 1.5}}>{action}</Box>}
      </AccordionDetails>
    </Accordion>
  )
}

/**
 * "What to upload, and how this data works" — seven disclosure rows, all
 * CLOSED on arrival. This page was rebuilt specifically to stop showing
 * a wall of prose before the merchant saw a single number about their own
 * account, so nothing here auto-opens, for any merchant, at any coverage
 * level.
 *
 * `coveragePct` is the module-wide sorter-coverage figure (dwsStats().
 * coveragePct in state/dwsEvidence.js) — UNROUNDED, same reason as
 * everywhere else in this module: fmtPct() turns a real sub-1% figure into
 * 'Under 1%' rather than a misleading '0%', and rounding here would defeat
 * that.
 */
export default function DwsShotList({coveragePct, noMerchantCount, skuCount, onOpenUpload}) {
  const [open, setOpen] = React.useState({})
  const keys = DWS_STEPS.map((s) => s.k).concat(['keep', 'api'])
  const allOpen = keys.every((k) => open[k])

  const toggle = (k) => () => setOpen((prev) => ({...prev, [k]: !prev[k]}))
  const toggleAll = () => setOpen(allOpen ? {} : keys.reduce((acc, k) => Object.assign(acc, {[k]: true}), {}))

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1}}>
        {/* Overridden locally to SIZE.md — sectionTitleSx's SIZE.xl is the
            larger of two competing in-content heading sizes this parity pass
            removes; patterns/index.js itself is shared and stays untouched. */}
        <Typography sx={{...sectionTitleSx, fontSize: SIZE.md}}>What to upload, and how this data works</Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 'auto'}}>
          {fmtInt(noMerchantCount)} of {fmtInt(skuCount)} SKUs still need yours
        </Typography>
        <Button
          size='small'
          onClick={toggleAll}
          startIcon={allOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          sx={secondaryBtnSx}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </Button>
      </Box>

      {DWS_STEPS.map((step) => {
        const n = (step.slots || []).length
        return (
          <Row
            key={step.k}
            icon={step.icon}
            title={step.title}
            chip={step.chip || (n === 1 ? '1 photo' : `${fmtInt(n)} photos`)}
            chipTone={step.chipTone || 'neutral'}
            body={step.body}
            expanded={!!open[step.k]}
            onToggle={toggle(step.k)}
            action={step.k === 'vid' ? (
              <Button size='small' startIcon={<UploadOutlinedIcon />} onClick={onOpenUpload} sx={secondaryBtnSx}>
                Open the upload form
              </Button>
            ) : null}
          />
        )
      })}

      <Row
        icon='timer'
        title='How long your records are kept'
        stat='10 per SKU · 6 months'
        body={DWS_RETAIN}
        expanded={!!open.keep}
        onToggle={toggle('keep')}
      />
      <Row
        icon='plug'
        title='Why some shipments have no sorter reading'
        stat={`${fmtPct(coveragePct)} scanned`}
        body={'Sorter images arrive only when the courier’s hub scans the parcel, and Prozo does not ingest them over an API. '
          + DWS_API_TIP
          + ' Nothing you do moves that figure. Your own uploads are the half you control.'}
        expanded={!!open.api}
        onToggle={toggle('api')}
      />
    </Box>
  )
}
